<script>
  //// imports
  import { onMount } from "svelte";
  import { writable } from "svelte/store";
  import { pmfilters } from "lib/stores.js";
  import { fetchWithSession } from "../../../../lib/util/fetchWithSession";
  import { filtersConfigStore } from "@stores/filtersConfigStores.js";
  import { getApplicableFilters } from "../../../../lib/util/getApplicableFilters";

  import {
    Accordion,
    SectionWrapper,
    Button,
    TextInput,
  } from "paxray-components-svelte";
  import FlowPlotter from "./components/FlowPlotter.svelte";

  import ProcessSelection from "./components/processSelection.svelte";
  import ContentSwitcher from "lib/components/ContentSwitcher.svelte";
  import Modal from "lib/components/Modal.svelte";
  import { useSvelteFlow } from "@xyflow/svelte";
  import AutomationAnalyzerSideMenu from "./components/AutomationAnalyzerSideMenu.svelte";
  import { FIT_SCREEN_ICON, FULLSCREEN_ICON } from "@utils/icons.js";

  //// data
  export let analysisid;
  export let processData = [];
  export let selectedRowIds = [];
  export let selectedRows;
  export let detailView = true;
  export let view = 1;

  let casesSelected = false;

  const { fitView, zoomIn, zoomOut, getZoom } = useSvelteFlow();
  let zoomLevel = writable((getZoom() * 100).toFixed(0));
  let initialZoomValue = writable(0);
  let fullscreen = false;

  let selectedNode = "";
  let selectedNodes = [];
  let expandedNodes = [];
  let fullGraphView = 0;
  let fullNodeInfo = true;
  let triggerUpdate = false;
  let nvariants = 1;
  let minFrequency = 0;
  let graph = {
    executionFrequency: 0,
    nodes: [],
    edges: [],
    variants: [],
    subgraphCluster: [],
    allsubgraphs: [],
    uniquePrograms: [],
  };

  let editProcessModalOpen = false;
  let editProcessId = 0;
  let editProcessName = "";
  let clusteringModalOpen = false;
  let nprocesses = 2;
  let showToast = false;

  $: if (view === 0) {
    detailView = false;
  }

  $: {
    if (
      (triggerUpdate || $pmfilters) &&
      $filtersConfigStore.storeLoaded &&
      selectedRowIds.length > 0
    ) {
      loadCluster(selectedRowIds, fullGraphView, expandedNodes);
    }
  }

  async function processGraphView() {
    casesSelected = true;
    await getCasesProcessGraph(nvariants);
    setTimeout(() => {
      fitView();
      updateZoomLevel();
    }, 100);
  }

  function toggleFullscreen() {
    fullscreen = !fullscreen;
  }

  async function incrementZoom() {
    await zoomIn();
    updateZoomLevel();
  }
  async function decrementZoom() {
    await zoomOut();
    updateZoomLevel();
  }
  async function updateZoomLevel() {
    zoomLevel.set((getZoom() * 100).toFixed(0));
  }

  function loadCluster(clusterids, fullGraphView = false, expandedNodes = []) {
    const applicableFilters = getApplicableFilters($pmfilters[analysisid]);
    const filterString = JSON.stringify(applicableFilters);
    if (clusterids && clusterids.length) {
      fetchWithSession(
        `/api/analysis/automation/getAutomationTraces?analysisid=` +
          analysisid +
          `&fullgraph=` +
          Boolean(fullGraphView) +
          `&clusterids=` +
          JSON.stringify(clusterids) +
          `&expandednodes=` +
          JSON.stringify(expandedNodes) +
          `&filter=${filterString}`,
      )
        .then((response) => response.json())
        .then((data) => {
          graph = data;
          if (data.nodes.length === 0) showToast = true;
          triggerUpdate = false;
        });
    }
  }

  function mergeProcesses(clusterids) {
    fetchWithSession(`/api/analysis/automation/applyClustering`, {
      method: "POST",
      body: JSON.stringify({
        analysisid: analysisid,
        clusters: clusterids,
        nclusters: nprocesses,
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.status == "success") {
          automationTraces();
        }
      });
  }

  function renameCluster() {
    fetchWithSession(`/api/analysis/automation/renameCluster`, {
      method: "POST",
      body: JSON.stringify({
        analysisid: analysisid,
        cluster: editProcessId,
        name: editProcessName,
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.status == "success") {
          automationTraces();
        }
      });
  }

  function automationTraces() {
    fetchWithSession(
      `/api/analysis/automation/automationTraces/` + analysisid,
      {},
    )
      .then((response) => response.json())
      .then((data) => {
        processData = data;
        if (selectedRowIds.length == 0 && processData.length > 0)
          selectedRowIds = [processData[0].id];
      });
  }

  // function selectedProcessName(selectedRowIds) {
  //   if (selectedRowIds.length == 0) return "No process ";
  //   const process = processData.find((p) => p.id == selectedRowIds);
  //   if (process) return process.name;
  // }

  onMount(() => {
    setTimeout(() => {
      const initialZoom = (getZoom() * 100).toFixed(0);
      zoomLevel.set(initialZoom);
      initialZoomValue.set(initialZoom);
    }, 500);
  });
</script>

<SectionWrapper {fullscreen} fitScreen={true}>
  <div class="main_wrapper" slot="main">
    <div class="canvas">
      <FlowPlotter
        nodesData={graph["nodes"]}
        edgesData={graph["edges"]}
        variants={graph["variants"]}
        subgraphs={graph["subgraphCluster"]}
        width="100%"
        height="100%"
        bind:selectedNode
        bind:selectedNodes
        bind:expandedNodes
        {fullGraphView}
        {fullNodeInfo}
        bind:zoomLevel={$zoomLevel}
        bind:triggerUpdate
        {nvariants}
        {minFrequency}
        {updateZoomLevel}
        executionFrequency={graph["executionFrequency"]}
      ></FlowPlotter>
    </div>
    <div class="menu_overlay bottom">
      <div class="button_wrapper">
        <ContentSwitcher
          options={["Activities", "Interactions"]}
          bind:selected={fullGraphView}
        />
      </div>
      <div class="button_wrapper">
        <Button
          kind="plus-minus"
          defaultValue={1}
          minValue={1}
          text="Variants"
          bind:value={nvariants}
          increment={(value) => value + 1}
          decrement={(value) => Math.max(1, value - 1)}
        />
        <Button
          kind="plus-minus"
          disabled={casesSelected}
          defaultValue={0}
          text="Min. Frequency"
          bind:value={minFrequency}
          increment={(i) => {
            if (i < 3) return i + 1;
            else {
              return Math.min(Math.round(i * 1.2), 100);
            }
          }}
          decrement={(i) => {
            if (i <= 3) return Math.max(0, i - 1);
            else return Math.round(i / 1.2);
          }}
          unit="%"
        />
      </div>
      <div class="button_wrapper">
        <Button
          kind="plus-minus"
          text="Zoom"
          bind:value={$zoomLevel}
          unit="%"
          reset={() => {
            fitView();
            updateZoomLevel();
          }}
          increment={incrementZoom}
          decrement={decrementZoom}
        />
        <Button
          kind="secondary"
          icon={FIT_SCREEN_ICON}
          on:click={() => {
            fitView();
            updateZoomLevel();
          }}
        />
        <Button
          kind="secondary"
          icon={FULLSCREEN_ICON}
          on:click={toggleFullscreen}
        />
      </div>
    </div>
  </div>
  <div class="menu" slot="menu">
    <ContentSwitcher options={["Table", "Flowchart"]} bind:selected={view} />
    <Accordion heading={"Process Selection"} open={true}>
      <ProcessSelection
        processes={processData}
        bind:selectedProcesses={selectedRowIds}
        editProcess={(id, name) => {
          editProcessId = id;
          editProcessName = name;
          editProcessModalOpen = true;
        }}
        mergeProcesses={() => {
          clusteringModalOpen = true;
        }}
      />
    </Accordion>
    <AutomationAnalyzerSideMenu
      rows={processData}
      {selectedRowIds}
      {selectedRows}
      {detailView}
    />
  </div>
</SectionWrapper>

<Modal
  open={editProcessModalOpen}
  modalHeading="Edit Process"
  primaryButtonText="Save"
  secondaryButtonText="Cancel"
  preventCloseOnClickOutside
  actions
  autowidth
  on:click:button--secondary={() => {
    editProcessModalOpen = false;
  }}
  on:open
  on:close={() => {
    editProcessModalOpen = false;
  }}
  primaryButtonAction={() => {
    renameCluster();
    editProcessModalOpen = false;
  }}
>
  <TextInput
    labelText="Name"
    placeholder="Enter name for process"
    bind:value={editProcessName}
  />
</Modal>

<Modal
  open={clusteringModalOpen}
  modalHeading="Split / Merge Processes"
  primaryButtonText="Apply"
  secondaryButtonText="Cancel"
  preventCloseOnClickOutside
  actions
  autowidth
  on:click:button--secondary={() => {
    clusteringModalOpen = false;
  }}
  on:open
  on:close={() => {
    clusteringModalOpen = false;
  }}
  primaryButtonAction={() => {
    mergeProcesses(selectedRowIds);
    clusteringModalOpen = false;
  }}
>
  Please specify how manxy processes you want to split the selected process(es)
  into:
  <TextInput
    labelText="Number Of Processes"
    placeholder="Number of Processes"
    bind:value={nprocesses}
  />
</Modal>

<style>
  .button_wrapper {
    display: flex;
    gap: 0.5rem;
    align-items: center;
  }

  .menu {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    height: calc(100vh - 6rem);
    overflow-x: scroll;
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */
  }
  .menu::-webkit-scrollbar {
    display: none;
  }

  .main_wrapper {
    position: relative;
    height: 100%;
    width: 100%;
    min-width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }

  .menu_overlay {
    position: absolute;
    width: 100%;
    z-index: 1; /* Stellt sicher, dass das Overlay über dem Canvas liegt */
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .bottom {
    bottom: 0;
  }

  .canvas {
    height: 100%;
    min-height: 100%;
    width: 100%;
    z-index: 0; /* Platziert das Canvas hinter den Overlays */
    display: flex;
    justify-content: center;
    align-items: center;
  }
</style>
